import React, { useEffect, useMemo } from 'react';
import { Flex, SpacingStack, TextV2 } from '@withjoy/joykit';
import { AdminSidebarComponentProps } from '../../JsonComponent.types';
import { Trash } from '@withjoy/joykit/icons';
import { useGoToReorderView } from '../../hooks/useJsonComponentNavigation';
import { useTranslation } from '@shared/core';
import { useEventInfo } from '@shared/utils/eventInfo';
import { PlaceToStayComponent } from './PlaceToStayComponent.types';
import { ComponentType } from '@graphql/generated';
import { pxToRem } from '@withjoy/joykit/theme';
import { SaveCancelButtons } from '@website-editor/features/custom-page/components/SaveCancelButtons/SaveCancelButtons';
import { useCustomComponentStateContext } from '@website-editor/features/custom-page/context/CustomComponentStateContext/CustomComponentState.context';
import { useAccommodationData } from './hooks/useAccommodationData';
import { usePlaceToStayForm } from './hooks/usePlaceToStayForm';
import { PlaceToStayFormHeader } from './components/PlaceToStayFormHeader';
import { PlaceToStayHotelSearch } from './components/PlaceToStayHotelSearch';
import { PlaceToStayManualFields } from './components/PlaceToStayManualFields';
import { PlaceToStayNoteField } from './components/PlaceToStayNoteField';
import { MatchedHotelCard } from './components/MatchedHotelCard';
import { useEventPlaces } from '@shared/utils/eventPlace';
import { distanceInMiles } from '@shared/utils/distanceInMiles';
import { useAuth } from '@shared/components/AuthProvider';
import { usePlaceToStayComponentTelemetry } from './PlaceToStayComponent.telemetry';
import { useEventCallback } from '@shared/utils/hooks/useEventCallback';

const PAGE_NAME = 'Custom page';

export const PlaceToStayComponentEdit = (props: AdminSidebarComponentProps) => {
  const { tq } = useTranslation('placeToStayComponent');
  const { goToReorderView } = useGoToReorderView();
  const { eventInfo } = useEventInfo();
  const { setShowDeletePopup, setDeleteComponentDetails } = useCustomComponentStateContext();
  const { isSuperAdmin } = useAuth();
  const telemetry = usePlaceToStayComponentTelemetry();

  const { isEditMode } = props;
  const placeToStayContent: PlaceToStayComponent | null = props.componentData.containerContent.content;
  const accommodationPlaceId = placeToStayContent?.accommodationPlaceId || '';
  const isOwnContainer = props.componentData.containerContent.eventId === eventInfo?.eventId;

  const { accommodationsMap, loading: loadingAccommodation } = useAccommodationData();
  const accommodation = accommodationPlaceId ? accommodationsMap.get(accommodationPlaceId) ?? null : null;
  const { origin } = useEventPlaces();

  // Track edit item clicked when component opens in edit mode
  useEffect(() => {
    if (isEditMode && accommodation) {
      telemetry.editItemClicked(PAGE_NAME, {
        isSuperAdmin,
        accommodationPlaceId: accommodation.id,
        displayName: accommodation.name,
        customName: accommodation.customName,
        url: accommodation.url,
        customUrl: accommodation.customUrl,
        accommodationAction: 'accommodationEdit',
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEditMode, accommodation?.id]);

  const { formik, loading, loadingLocation, matchedHotel, handleSearchLocationV1, accommodationDistances } = usePlaceToStayForm({
    accommodation,
    accommodationPlaceId,
    isEditMode: !!isEditMode,
    isOwnContainer: !!isOwnContainer,
    eventHandle: props.eventHandle,
    containerId: props.containerId,
    componentDataId: props.componentData.id,
    sortOrder: props.componentData?.sortOrder,
    goToReorderView,
  });

  const hasMatchedHotel = !!matchedHotel && !!formik.values.joyPlaceId;
  const hasLocationSelected = typeof formik.values.latitude === 'number' && typeof formik.values.longitude === 'number';
  const showManualFields = !hasMatchedHotel && hasLocationSelected;

  const milesToVenue = useMemo(() => {
    // Calculate distance in miles like Accommodation.utils.tsx
    if (accommodation?.lat && accommodation?.long && origin) {
      return distanceInMiles(origin, { lat: accommodation.lat, lng: accommodation.long });
    }
    return null;
  }, [accommodation?.lat, accommodation?.long, origin]);

  const handleSave = useEventCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      e.preventDefault();
      telemetry.editItemSubmit(PAGE_NAME, {
        isSuperAdmin,
        accommodationPlaceId: accommodation?.id,
        zhId: accommodation?.zhId ?? null,
        accommodationAction: isEditMode ? 'edited' : 'added',
        usingCustomLink: !!formik.values.customUrl,
        customLink: formik.values.customUrl || '',
      });
      formik.handleSubmit();
    },
    [telemetry, isSuperAdmin, accommodation?.id, accommodation?.zhId, isEditMode, formik.values.customUrl, formik.handleSubmit],
  );

  const handleCancel = useEventCallback(() => {
    telemetry.editItemCancel(PAGE_NAME, { isSuperAdmin });
    goToReorderView();
  }, [telemetry, isSuperAdmin, goToReorderView]);

  const handleDeleteClick = useEventCallback(() => {
    if (props.componentData.id) {
      telemetry.deleteItemDialog(PAGE_NAME, {
        isSuperAdmin,
        accommodationPlaceId,
      });
      setShowDeletePopup(true);
      setDeleteComponentDetails({
        id: props.componentData.id,
        title: tq`Are you sure you want to delete this place to stay?`,
        subTitle: tq`The contents of this section will be lost. This action can't be undone.`,
        deleteCta: tq`Delete Place to Stay`,
        cancelCta: tq`Cancel`,
        successMessage: tq`Success! Place to stay has been deleted.`,
        errorMessage: tq`An error occurred while deleting this place to stay. Please try again.`,
        type: props.componentData.containerContent.type ?? ComponentType.jsonDataBlob,
        accommodationPlaceId,
      });
    }
  }, [telemetry, isSuperAdmin, accommodationPlaceId, props.componentData.id, props.componentData.containerContent.type, setShowDeletePopup, setDeleteComponentDetails, tq]);

  if (isEditMode && loadingAccommodation) {
    return (
      <Flex
        flexDirection="column"
        paddingX={8}
        paddingY={6}
        justifyContent="center"
        alignItems="center"
        height="100%"
      >
        <TextV2 typographyVariant="body1">{tq`Loading...`}</TextV2>
      </Flex>
    );
  }

  return (
    <Flex
      flexDirection="column"
      width="100%"
      height="100%"
    >
      <PlaceToStayFormHeader
        isEditMode={!!isEditMode}
        onBackClick={goToReorderView}
      />
      <form
        onSubmit={formik.handleSubmit}
        style={{ width: '100%', display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}
      >
        <Flex
          flexDirection="column"
          paddingX={8}
          paddingTop={6}
          overflow="auto"
          flex={1}
          minHeight={0}
        >
          <SpacingStack spacing={7}>
            <PlaceToStayHotelSearch
              formik={formik}
              loadingLocation={loadingLocation}
              onLocationChange={handleSearchLocationV1}
            />

            {hasMatchedHotel && matchedHotel && (
              <MatchedHotelCard
                hotel={matchedHotel}
                distances={accommodationDistances}
                milesToVenue={milesToVenue}
              />
            )}

            {showManualFields && <PlaceToStayManualFields formik={formik} />}

            {hasLocationSelected && <PlaceToStayNoteField formik={formik} />}
          </SpacingStack>

          {isEditMode && (
            <Flex
              justifyContent="center"
              alignItems="center"
            >
              <Flex
                gap={3}
                cursor="pointer"
                alignItems="center"
                onClick={handleDeleteClick}
              >
                <Trash
                  size={24}
                  color="negative6"
                />
                <TextV2
                  typographyVariant="button2"
                  color="negative6"
                >
                  {tq`Delete`}
                </TextV2>
              </Flex>
            </Flex>
          )}
        </Flex>
        <Flex
          paddingX={pxToRem(24)}
          paddingBottom={pxToRem(24)}
        >
          <SaveCancelButtons
            onCancel={handleCancel}
            onSave={handleSave}
            isLoading={loading}
            isSaveDisabled={loadingLocation || loading || !formik.isValid}
          />
        </Flex>
      </form>
    </Flex>
  );
};
